import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { AddIcon, DeleteIcon } from "@/components/icons/action-icons";
import FormWrapper from "@/components/common/FormWrapper";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { usePageTitle } from "@/hooks/usePageTitle";
import { ENUM_PROJECT_KPI_STATUSES } from "@/types/(company)/projects.interface";
import { FieldArray, FormikProvider, useFormik } from "formik";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import * as yup from "yup";
import { Textarea } from "@/components/ui/textarea";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group";
import { cn } from "@/utils/cn";
import {
  useCreateProjectOKRMutation,
  useGetProjectOKRByIdQuery,
  useUpdateProjectOKRMutation,
} from "@/services/(company)/api.projects";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { PATHS } from "@/routes/paths";
import { hTabKey } from "@/components/HorizontalTabs";
import {
  okrIdKey,
  PROJECT_TAB_KEYS,
  projectIdKey,
} from "@/routes/company/routes.projects.constants";
import { Spinner } from "@/components/ui/v2/spinner";
import { Checkbox } from "@/components/ui/checkbox";
import { SectionTitle } from "@/components/ui/SectionTitle";
import { Loader as LoaderIcon, Percent } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePermissions } from "@/hooks/usePermissions";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import { AccessDenied } from "@/components/permission-guard";

const buildFormSchema = (t: TFunction) =>
  yup.object({
    name: yup.string().trim().required(t("projects:okr_objective_is_required")),
    description: yup.string().trim().optional(),
    status: yup
      .string()
      .oneOf(
        Object.values(ENUM_PROJECT_KPI_STATUSES),
        t("projects:invalid_status"),
      )
      .default(ENUM_PROJECT_KPI_STATUSES.TODO),
    isActive: yup.boolean().default(true),
    keyResults: yup
      .array()
      .of(
        yup.object({
          name: yup
            .string()
            .trim()
            .required(t("projects:key_result_name_is_required"))
            .max(300, t("projects:keep_it_under_300_characters")),
          progress: yup
            .number()
            .typeError(t("projects:progress_must_be_a_number"))
            .min(0, t("projects:progress_cannot_be_negative"))
            .max(100, t("projects:progress_cannot_exceed_100"))
            .default(0),
        }),
      )
      .min(1, t("projects:at_least_one_key_result_is_required"))
      .default([]),
  });
type Fields = yup.InferType<ReturnType<typeof buildFormSchema>>;
const fieldNames: Record<keyof Fields, keyof Fields> = {
  name: "name",
  description: "description",
  isActive: "isActive",
  status: "status",
  keyResults: "keyResults",
};

export const AddOKRForm = () => {
  const { t } = useTranslation(["projects", "common"]);
  const navigate = useNavigate();
  const { projectId, okrId } = useParams<{
    [projectIdKey]: string;
    [okrIdKey]: string;
  }>();
  const { can, isLoading: isPermissionsLoading } = usePermissions();

  const isUpdate = Boolean(projectId && okrId);
  usePageTitle(
    isUpdate ? t("projects:update_okrs") : t("projects:add_new_okrs"),
  );

  const { data: { data: kpi } = {}, isLoading } = useGetProjectOKRByIdQuery(
    { projectId: projectId!, okrId: okrId! },
    { skip: !projectId || !okrId },
  );
  const [addOKR, { isLoading: isCreating }] = useCreateProjectOKRMutation();
  const [updateOKR, { isLoading: isUpdating }] = useUpdateProjectOKRMutation();

  const formik = useFormik<Fields>({
    enableReinitialize: true,
    validationSchema: buildFormSchema(t),
    initialValues: {
      name: kpi?.name || "",
      isActive: kpi?.isActive ?? false,
      status: kpi?.status ?? ENUM_PROJECT_KPI_STATUSES.TODO,
      description: kpi?.description || "",
      keyResults: [{ name: "", progress: 0 }],
    },
    onSubmit: async (values) => {
      if (!projectId) return toast.error(t("projects:project_id_not_found"));
      const isUpdating = Boolean(projectId && okrId);
      if (isUpdating) {
        await updateOKR({ projectId, okrId: okrId!, payload: values })
          .unwrap()
          .then((res) => {
            navigate(
              PATHS.COMPANY.COMPANY_PROJECTS_PATHS.VIEW(projectId) +
                `?${hTabKey}=` +
                PROJECT_TAB_KEYS.Okrs,
            );
            toast.success(res.message);
          })
          .catch((e) => toast.error(e?.data?.message ?? e?.message));
      } else {
        await addOKR({ projectId, payload: values })
          .unwrap()
          .then((res) => {
            navigate(
              PATHS.COMPANY.COMPANY_PROJECTS_PATHS.VIEW(projectId) +
                `?${hTabKey}=` +
                PROJECT_TAB_KEYS.Okrs,
            );
            toast.success(res.message);
          })
          .catch((e) => toast.error(e?.data?.message ?? e?.message));
      }
    },
  });

  if (isPermissionsLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoaderIcon className="animate-spin text-primary" />
      </div>
    );
  }

  if (isUpdate && !can(PLATFORM_SECTION_ENUM.PROJECTS, "edit")) {
    return (
      <AccessDenied
        description={t("projects:you_don_t_have_permission_to_edit_okrs")}
        onBack={() => navigate(-1)}
      />
    );
  }

  if (!isUpdate && !can(PLATFORM_SECTION_ENUM.PROJECTS, "create")) {
    return (
      <AccessDenied
        description={t("projects:you_don_t_have_permission_to_create_okrs")}
        onBack={() => navigate(-1)}
      />
    );
  }

  return (
    <FormWrapper
      title={isUpdate ? t("projects:update_okr") : t("projects:add_okr")}
      onSubmit={formik.handleSubmit}
      loading={isCreating || isUpdating}
      subtitle={t(
        "projects:enter_the_details_of_the_objective_key_results_to_measur",
      )}
    >
      {isLoading ? (
        <Spinner />
      ) : (
        <form className="grid md:grid-cols-2 gap-4">
          <Field>
            <FieldLabel>{t("projects:objective_name")}</FieldLabel>
            <Input
              id={fieldNames.name}
              name={fieldNames.name}
              value={formik.values.name}
              onChange={formik.handleChange}
              placeholder={t("projects:e_g_deployment_frequency")}
              className={cn(
                formik.touched.name && formik.errors.name && "border-danger",
              )}
            />
            <FieldError>
              {formik.touched.name ? formik.errors.name : null}
            </FieldError>
          </Field>
          <Field>
            <FieldLabel>{t("projects:status")}</FieldLabel>
            <Select
              value={formik.values.status}
              onValueChange={(value) =>
                formik.setFieldValue(fieldNames.status, value)
              }
            >
              <SelectTrigger
                className={cn(
                  formik.touched.status &&
                    formik.errors.status &&
                    "border-danger",
                )}
              >
                <SelectValue placeholder={t("projects:select_a_status")} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {Object.values(ENUM_PROJECT_KPI_STATUSES).map((status) => (
                    <SelectItem key={status} value={status}>
                      {t(`projects:${status}`)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            <FieldError>{formik.errors.status}</FieldError>
          </Field>

          <FormikProvider value={formik}>
            <FieldArray name="keyResults">
              {({ push, remove }) => (
                <FieldGroup className="col-span-2 border p-4 rounded-sm max-h-max gap-4 flex flex-col">
                  <SectionTitle
                    title={t("projects:key_results")}
                    noMarginBottom
                  />

                  {formik.values.keyResults.map((kr, index) => (
                    <div
                      key={index}
                      className="flex flex-col md:flex-row gap-4 items-start"
                    >
                      {/* Title Field */}
                      <Field className="flex-1">
                        <FieldLabel>
                          {t("projects:key_result_n", { index: index + 1 })}
                        </FieldLabel>
                        <InputGroup className="h-12">
                          <InputGroupInput
                            name={`keyResults.${index}.name`}
                            value={kr.name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            placeholder={t(
                              "projects:e_g_reduce_api_latency_by_30",
                            )}
                            className={cn(
                              "h-full",
                              formik.touched.keyResults?.[index]?.name &&
                                (formik.errors.keyResults?.[index] as any)
                                  ?.name &&
                                "border-danger",
                            )}
                          />
                          <InputGroupAddon>
                            <div className="px-2 py-0.5 bg-primary text-white text-xs rounded-sxs">
                              {`KR-${index + 1}`}
                            </div>
                          </InputGroupAddon>
                        </InputGroup>
                      </Field>

                      {/* Progress Field */}
                      <Field className="w-full md:w-2/6">
                        <FieldLabel>
                          {t("projects:progress_percent")}
                        </FieldLabel>
                        <InputGroup className="h-12">
                          <InputGroupInput
                            type="number"
                            name={`keyResults.${index}.progress`}
                            value={kr.progress}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            placeholder="0"
                            className="h-full"
                          />
                          <InputGroupAddon>
                            <Percent size={14} />
                          </InputGroupAddon>

                          {/* Trash Button - only show if more than one KR exists, or just allow all to be deleted */}
                          <InputGroupButton
                            type="button"
                            title={t("common:buttons.remove")}
                            onClick={() => remove(index)}
                          >
                            <DeleteIcon size={18} className="text-danger" />
                          </InputGroupButton>
                        </InputGroup>
                      </Field>
                    </div>
                  ))}

                  {/* Add Button */}
                  <Button
                    type="button"
                    variant="outline-primary"
                    className="max-w-max"
                    size="sm"
                    onClick={() => push({ name: "", progress: 0 })}
                  >
                    <AddIcon className="text-primary me-1" size={16} />{" "}
                    {t("projects:add_key_result")}
                  </Button>

                  {/* Array-level Errors */}
                  {typeof formik.errors.keyResults === "string" && (
                    <FieldError>{formik.errors.keyResults}</FieldError>
                  )}
                </FieldGroup>
              )}
            </FieldArray>
          </FormikProvider>

          <Field className="col-span-2">
            <FieldLabel>{t("projects:description")}</FieldLabel>
            <Textarea
              id={fieldNames.description}
              name={fieldNames.description}
              value={formik.values.description}
              onChange={formik.handleChange}
              placeholder={t("projects:describe_what_this_kpi_is_for")}
            />
          </Field>
          <Field orientation="horizontal" className="col-span-2">
            <Checkbox
              id={fieldNames.isActive}
              checked={formik.values.isActive}
              onCheckedChange={(checked) =>
                formik.setFieldValue(fieldNames.isActive, checked)
              }
            />
            <FieldLabel htmlFor={fieldNames.isActive}>
              {t("projects:mark_as_active")}
            </FieldLabel>
          </Field>
        </form>
      )}
    </FormWrapper>
  );
};
